import { DriverState } from ".";
import { SelectOption } from "components/atoms/Select";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { config } from "config";

const default_page_size = config.PAGE_SIZE;
const initialState: DriverState = {
  tab: 0,
  count: 0,
  refresh: 0,
  drivers: [],
  driver: null,
  loading: true,
  driverOptions: [],
  current_filters: {},
  refreshLoader: false,
  filters: { page: 1, page_size: default_page_size },
  groupTrackings: [],
  groupTracking: null,
  groupTrackingCount: 0,
  groupTrackingFilters: { page: 1, page_size: default_page_size },
  driverGroupAddress: [],
  driverSubStatuses: [],
  driverAttendance: [],
  attendanceLoading: false,
  driversTodayStatus: {},
  driversReports: [],
  driversReportsCount: 0,
  driversReportsFilters: { page: 1, page_size: default_page_size },
};

export const driverSlice = createSlice({
  name: "driver",
  initialState,
  reducers: {
    clear: (state) => {
      state.driverOptions = [];
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setTab: (state, action: PayloadAction<number>) => {
      state.tab = action.payload;
    },
    addDriver: (state, action) => {
      state.drivers.unshift(action.payload);
    },
    updateDriver: (state, action) => {
      const { id, driver } = action.payload;
      state.drivers.every(({ _id }, i) => {
        if (id === _id) {
          state.drivers[i] = driver;
          return false;
        }
        return true;
      });
    },
    setDriver: (state, action) => {
      state.driver = action.payload;
    },
    setDrivers: (
      state,
      action: PayloadAction<{ drivers: any[]; count: number }>,
    ) => {
      const { count, drivers } = action.payload;
      let options: SelectOption[] = [];

      drivers.forEach(({ _id, last_name, first_name, status }: any) => {
        const option = {
          value: _id,
          label: `${first_name} ${last_name}`,
          status: status,
        };
        options.push(option);
      });

      state.count = count;
      state.drivers = drivers;
      state.refreshLoader = false;
      state.driverOptions = options;
    },
    setPartnerDrivers: (state, action) => {
      const drivers = action.payload;
      let options: SelectOption[] = [];
      drivers.forEach(({ _id, last_name, first_name, email, phone, profile_pic ,profile_logo}: any) =>
        options.push({
          value: _id,
          label: `${first_name} ${last_name}`,
          email: `${email}`,
          phone: `${phone}`,
          profileImage: profile_logo?.url|| profile_pic?.url,
        }),
      );
      state.driverOptions = options;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.refresh += 1;
      state.refreshLoader = true;
      state.filters.page = action.payload;
    },
    setGroupTrackingPage: (state, action: PayloadAction<number>) => {
      state.refresh += 1;
      state.refreshLoader = true;
      state.groupTrackingFilters.page = action.payload;
    },
    refresh: (state) => {
      state.refresh += 1;
      state.refreshLoader = true;
    },
    resetPage: (state) => {
      state.filters.page = 1;
      state.groupTrackingFilters.page = 1;
    },
    resetGroupTrackingFilters: (state) => {
      state.groupTrackingFilters = initialState.groupTrackingFilters;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
      state.refreshLoader = true;
    },
    setCurrentFilters: (state, action) => {
      state.current_filters = action.payload;
    },
    resetFilters: (state) => {
      state.refresh += 1;
      state.refreshLoader = true;
      state.filters = initialState.filters;
      state.current_filters = initialState.current_filters;
    },
    // Group Tracking actions
    setGroupTrackings: (
      state,
      action: PayloadAction<{ driverGroupTrackings: any[]; count: number }>,
    ) => {
      const { count, driverGroupTrackings } = action.payload;
      state.groupTrackingCount = count;
      state.groupTrackings = driverGroupTrackings;
      state.refreshLoader = false;
      let options: SelectOption[] = [];
      driverGroupTrackings.forEach(({ address }: any) => {
        if (address && address !== "" && address !== undefined) {
          options.push({ value: address, label: address });
        }
      });
      state.driverGroupAddress = options;
    },
    setGroupTracking: (
      state,
      action: PayloadAction<{ driverGroupTracking: any[] }>,
    ) => {
      const { driverGroupTracking } = action.payload;
      state.groupTracking = driverGroupTracking;
    },
    setGroupTrackingFilters: (state, action) => {
      state.groupTrackingFilters = action.payload;
      state.refreshLoader = true;
      state.refresh += 1;
    },
    addGroupTracking: (state, action) => {
      state.groupTracking.unshift(action.payload);
      state.groupTrackingCount += 1;
    },
    updateGroupTracking: (state, action) => {
      const { id, groupTracking } = action.payload;
      state.groupTrackings.every(({ _id }: any, i: number) => {
        if (id === _id) {
          state.groupTrackings[i] = groupTracking;
          return false;
        }
        return true;
      });
    },
    deleteGroupTracking: (state, action) => {
      const id = action.payload;
      state.groupTrackings = state.groupTrackings.filter(
        (item: any) => item._id !== id,
      );
      state.groupTrackingCount -= 1;
    },
    setDriverSubStatuses: (state, action) => {
      state.driverSubStatuses = action.payload;
      state.refreshLoader = false;
    },
    setDriverAttendance: (state, action) => {
      state.driverAttendance = action.payload;
      state.attendanceLoading = false;
    },
    setDriverAttendanceLoading: (state, action: PayloadAction<boolean>) => {
      state.attendanceLoading = action.payload;
    },
    setDriversTodayStatus: (state, action: PayloadAction<Record<string, { status: string; off_dates: string[] }>>) => {
      state.driversTodayStatus = action.payload;
    },
    addDriverSubStatus: (state, action) => {
      state.driverSubStatuses.push(action.payload);
    },
    updateDriverSubStatus: (state, action) => {
      const { id, log } = action.payload;
      state.driverSubStatuses.every(({ _id }, i) => {
        if (id === _id) {
          console.log(log, "log");
          state.driverSubStatuses[i] = log;
          return false;
        }
        return true;
      });
    },
    setDriversReports: (state, action) => {
      state.driversReports = action.payload.reports;
      state.driversReportsCount = action.payload.count;
      state.refreshLoader = false;
    },
    setDriversReportsFilters: (state, action) => {
      state.driversReportsFilters = action.payload;
      state.refreshLoader = true;
    },
    addDriversReport: (state, action) => {
      state.driversReports.unshift(action.payload);
      state.driversReportsCount += 1;
    },
  },
});

const driverReducer = driverSlice.reducer;

export const driverActions = driverSlice.actions;
export default driverReducer;
